import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';

const ChatbotInsights = () => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const data = await api.request('/admin/chatbot-insights');
      setInsights(data);
    } catch (err) {
      console.error('Failed to fetch chatbot insights:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">{t('chatbotInsights.title')}</h1>
        <p className="text-gray-300 mt-1">{t('chatbotInsights.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('chatbotInsights.totalConversations')}</p>
          <p className="text-xl font-bold text-levelup-dark">{insights?.stats?.total_conversations || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('chatbotInsights.uniqueUsers')}</p>
          <p className="text-xl font-bold text-levelup-dark">{insights?.stats?.unique_users || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('chatbotInsights.avgResponseTime')}</p>
          <p className="text-xl font-bold text-levelup-dark">{insights?.stats?.avg_response_time_ms || 0}ms</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('chatbotInsights.last24h')}</p>
          <p className="text-xl font-bold text-levelup-teal">{insights?.stats?.conversations_24h || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('chatbotInsights.last7d')}</p>
          <p className="text-xl font-bold text-levelup-accent">{insights?.stats?.conversations_7d || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Questions */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <h2 className="text-lg font-bold text-levelup-dark mb-4">{t('chatbotInsights.topQuestions')}</h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {insights?.top_questions?.length > 0 ? (
              insights.top_questions.map((q, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 flex-1 pr-4 line-clamp-2">{q.user_message}</p>
                  <span className="bg-levelup-accent text-levelup-darker px-3 py-1 rounded-full text-sm font-medium">
                    {q.count}x
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No questions yet</p>
            )}
          </div>
        </div>

        {/* Usage by Domain */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <h2 className="text-lg font-bold text-levelup-dark mb-4">{t('chatbotInsights.usageByDomain')}</h2>
          <div className="space-y-3">
            {insights?.usage_by_domain?.length > 0 ? (
              insights.usage_by_domain.map((d, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{d.domain}</p>
                  <span className="bg-levelup-teal text-white px-3 py-1 rounded-full text-sm font-medium">
                    {d.count} chats
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No domain data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Conversations */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <h2 className="text-lg font-bold text-levelup-dark mb-4">{t('chatbotInsights.recentConversations')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('chatbotInsights.user')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('chatbotInsights.question')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('chatbotInsights.response')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('chatbotInsights.time')}</th>
              </tr>
            </thead>
            <tbody>
              {insights?.recent_conversations?.length > 0 ? (
                insights.recent_conversations.slice(0, 10).map((c, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-700">{c.user_email?.split('@')[0] || 'Unknown'}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">{c.user_message}</td>
                    <td className="py-3 px-4 text-sm text-gray-500 max-w-xs truncate">{c.assistant_response}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">No conversations yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Feedback Analytics Component (Admin only)

export default ChatbotInsights;
